import { Component } from '@theme/component';
// VariantUpdateEvent is used for JSDoc type annotations
// eslint-disable-next-line no-unused-vars
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/** The per-load class the app tags its message with, and looks it up by before every render. */
const APP_MESSAGE_CLASS = /^estimator-message-[a-z0-9]+$/;

/**
 * @param {Element} element
 * @returns {boolean} Whether the app rendered the element, rather than this component copying it.
 */
const isAppMessage = (element) =>
  [...element.classList].some((name) => APP_MESSAGE_CLASS.test(name));

/**
 * A copy of the last message the app rendered. Held for the page, like the app itself: the morph
 * a colorway swap does can replace this element along with the block, and the new one still has
 * to stand the copy in.
 * @type {Element | undefined}
 */
let lastMessage;

/**
 * Keeps the Delivery Estimator app block in step with the selected variant.
 *
 * The app reads the variant from the product form's hidden input while the size picker's own
 * `change` event is still bubbling, which is before the theme has written the new id into it —
 * so every render it does describes the previously selected variant. That leaves the delivery
 * promise standing on a sold-out size, and tears it down for good on the next available one.
 * Re-firing `change` once the input holds the new id hands the app the variant it should have
 * read, and it renders or removes its own message from there.
 *
 * Whenever the block is left empty, a copy of the last message the app rendered stands in until
 * it renders again. That keeps the block's height through the few hundred milliseconds each of
 * its re-renders takes, and through a colorway swap: the morph empties the block, and the app
 * can't render for the swapped-in product at all — it reads the product once, on load, from an
 * app embed outside `main`. The promise comes from shipping rules, which colorways share.
 */
class DeliveryEstimator extends Component {
  /** The variant the app has already been re-synced for. @type {string | undefined} */
  #syncedVariantId;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    this.closest('.shopify-section, dialog')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal: this.#abortController.signal },
    );

    const block = this.closest('.product-details')?.querySelector('.estimator-message--block');
    if (!block) return;

    this.#observer = new MutationObserver(() => this.#syncStandIn(block));
    this.#observer.observe(block, { childList: true });
    this.#syncStandIn(block);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  /** @type {EventListener} */
  #handleVariantUpdate = (event) => {
    const variant = /** @type {VariantUpdateEvent} */ (event).detail.resource;
    const details = this.closest('.product-details');
    if (!details) return;

    // Panel-level state, read by the store-locator row as well as this block, so it has to be
    // kept up whether or not the app is on the page. Server-rendered on load and toggled here
    // because the app's own out-of-stock handling only catches up a few hundred milliseconds
    // after the selection.
    details.toggleAttribute('data-variant-unavailable', !variant?.available);

    if (!variant) return;
    this.#resync(details, String(variant.id));
  };

  /**
   * Re-fires `change` on the product form so the app re-reads the variant.
   * @param {Element} details - The product details panel holding the app block.
   * @param {string} variantId - The newly selected variant's id.
   */
  #resync(details, variantId) {
    if (this.#syncedVariantId === variantId) return;
    if (!details.querySelector('.estimator-message--block')) return;

    const input = details.querySelector('product-form-component input[name="id"]');

    // The form carries the previous variant on the first of the two updates a selection fires;
    // the second arrives with the server HTML, by which point it is settled.
    if (!(input instanceof HTMLInputElement) || input.value !== variantId) return;

    this.#syncedVariantId = variantId;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Copies each message the app renders, clears the stand-in once it has, and puts the stand-in
   * back whenever the block is emptied. A sold-out variant empties it too; the stand-in is hidden
   * along with the block there.
   * @param {Element} block - The app's message container.
   */
  #syncStandIn(block) {
    const messages = [...block.children].filter((child) => child.matches('.estimator-message'));
    const rendered = messages.find(isAppMessage);

    if (rendered) {
      lastMessage = this.#toStandIn(rendered);
      for (const message of messages) {
        if (message !== rendered) message.remove();
      }
      return;
    }

    if (messages.length === 0 && lastMessage) block.append(lastMessage.cloneNode(true));
  }

  /**
   * @param {Element} message - A message the app rendered.
   * @returns {Element} A copy without the app's tag, which it would otherwise find and remove.
   */
  #toStandIn(message) {
    const copy = /** @type {Element} */ (message.cloneNode(true));
    copy.classList.remove(...[...copy.classList].filter((name) => APP_MESSAGE_CLASS.test(name)));
    return copy;
  }
}

if (!customElements.get('delivery-estimator')) {
  customElements.define('delivery-estimator', DeliveryEstimator);
}
